import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import api from '../../../utils/api';
import { validateVersion, getVersionError } from '../../../utils/validation';

const AddFirmwareVersionModal = ({ open, onClose, onConfirm, firmwareVersionData, editMode = false, deviceTypes = [] }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    version: '',
    file: null,
    deviceType: '',
    note: '',
  });

  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Prepare device type options for Select component
  const deviceTypeOptions = useMemo(() => {
    // API returns array: [{ id: 1, name: "Type 1" }, { id: 2, name: "Type 2" }, ...]
    // Convert id to string for consistency with formData.deviceType (which is string)
    return (deviceTypes || []).map(dt => ({
      id: String(dt.id),
      name: dt.name
    }));
  }, [deviceTypes]);

  useEffect(() => {
    if (editMode && firmwareVersionData) {
      setFormData({
        id: firmwareVersionData.id,
        version: firmwareVersionData.version || '',
        file: null, // File is not pre-populated in edit mode
        deviceType: firmwareVersionData.deviceType !== undefined && firmwareVersionData.deviceType !== null ? String(firmwareVersionData.deviceType) : '',
        note: firmwareVersionData.note || '',
      });
      // Set existing file name if available
      setFileName(firmwareVersionData.fileName || '');
    } else if (!editMode) {
      setFormData({
        version: '',
        file: null,
        deviceType: '',
        note: '',
      });
      setFileName('');
    }
    setErrors({});
  }, [editMode, firmwareVersionData, open]);

  // Check if all required fields are filled
  const isFormValid = () => {
    if (editMode) {
      // In edit mode, file is optional
      return formData.version.trim() !== '' && formData.deviceType !== '';
    } else {
      // In add mode, all fields including file are required
      return formData.version.trim() !== '' && formData.file !== null && formData.deviceType !== '';
    }
  };

  const validateForm = async () => {
    const newErrors = {};

    // Validate version format using validation utility
    const versionError = getVersionError(formData.version, t);
    if (versionError) {
      newErrors.version = versionError;
    }

    // Validate file is selected in add mode
    if (!editMode && !formData.file) {
      newErrors.file = t('firmwareVersions.validation.fileRequired');
    }

    // Check for duplicate filename
    if (formData.file) {
      try {
        const response = await api.get('/firmwareversions');
        const existingFiles = response.items || response;

        // Check if filename already exists (excluding current item in edit mode)
        const isDuplicate = existingFiles.some(item => {
          const isSameFile = item.fileName === formData.file.name;
          const isDifferentId = editMode ? item.id !== formData.id : true;
          return isSameFile && isDifferentId;
        });

        if (isDuplicate) {
          newErrors.file = t('firmwareVersions.validation.fileNameDuplicate');
        }
      } catch (error) {
        console.error('Error checking duplicate filename:', error);
      }
    }

    // Validate device type is selected
    if (!formData.deviceType) {
      newErrors.deviceType = t('firmwareVersions.validation.deviceTypeRequired');
    }

    // Validate note length
    if (formData.note && formData.note.length > 255) {
      newErrors.note = t('firmwareVersions.validation.noteMaxLength', { max: 255 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation for version field
    if (name === 'version') {
      // Only validate format if user has entered something
      if (value.trim()) {
        // Check max length
        if (value.length > 50) {
          setErrors((prev) => ({ ...prev, version: t('validation.versionMaxLength', { max: 50 }) }));
        }
        // Check invalid format
        else if (!validateVersion(value)) {
          setErrors((prev) => ({ ...prev, version: t('validation.versionInvalidFormat') }));
        } else {
          setErrors((prev) => ({ ...prev, version: '' }));
        }
      } else {
        // Clear error when field is empty (don't show "required" during typing)
        setErrors((prev) => ({ ...prev, version: '' }));
      }
    }

    // Real-time validation for note field
    if (name === 'note') {
      if (value.length > 255) {
        setErrors((prev) => ({ ...prev, note: t('firmwareVersions.validation.noteMaxLength', { max: 255 }) }));
      } else {
        // Clear note error when length is valid
        setErrors((prev) => ({ ...prev, note: '' }));
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file extension
      const allowedExtensions = ['.bin', '.hex', '.fw', '.apk', '.zip'];
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

      if (!allowedExtensions.includes(fileExtension)) {
        setErrors((prev) => ({
          ...prev,
          file: t('firmwareVersions.validation.invalidFileType', {
            types: allowedExtensions.join(', ')
          })
        }));
        // Reset file input
        e.target.value = '';
        return;
      }

      // Validate file size (50 MB)
      const maxSize = 50 * 1024 * 1024; // 50 MB in bytes
      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          file: t('firmwareVersions.validation.fileTooLarge', { maxSize: '50 MB' })
        }));
        // Reset file input
        e.target.value = '';
        return;
      }

      // Extract filename without extension as version name
      const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;

      setFormData((prev) => ({
        ...prev,
        file: file,
        // Auto-fill version field with filename (without extension) if version is empty
        version: prev.version.trim() === '' ? fileNameWithoutExt : prev.version
      }));
      setFileName(file.name);
      // Clear file error when user selects a valid file
      if (errors.file) {
        setErrors((prev) => ({ ...prev, file: '' }));
      }
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const isValid = await validateForm();

    if (!isValid) {
      setLoading(false);
      return;
    }

    // Create the payload to send
    const payload = {
      version: formData.version,
      deviceType: parseInt(formData.deviceType, 10), // Convert string to number (short)
      note: formData.note,
    };

    // Add file only if it exists
    if (formData.file) {
      payload.file = formData.file;
    }

    // Add ID for edit mode
    if (editMode && formData.id) {
      payload.id = formData.id;
    }

    onConfirm(payload);
    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      id="firmware-version-dialog"
      data-testid="firmware-version-dialog"
    >
      <DialogTitle>
        {editMode ? t('firmwareVersions.editTitle') : t('firmwareVersions.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Input
            id="version"
            name="version"
            data-testid="firmwareversion-modal-input-version"
            label={t('firmwareVersions.version')}
            value={formData.version}
            onChange={handleChange}
            error={!!errors.version}
            helperText={errors.version || t('firmwareVersions.versionFormatHint')}
            required
            disabled={loading}
          />

          <Box>
            <Tooltip title={fileName || t('firmwareVersions.chooseFile')} placement="top">
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {fileName ? fileName : t('firmwareVersions.chooseFile')}
                <input
                  type="file"
                  hidden
                  accept=".bin,.hex,.fw,.apk,.zip"
                  onChange={handleFileChange}
                  id="file"
                  name="file"
                  data-testid="firmwareversion-modal-input-file"
                />
              </Button>
            </Tooltip>
            {errors.file && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.file}
              </Typography>
            )}
            {editMode && fileName && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('firmwareVersions.currentFile')}: {fileName}
              </Typography>
            )}
            {!editMode && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('firmwareVersions.fileHint')}
              </Typography>
            )}
            {editMode && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('firmwareVersions.fileUpdateHint')}
              </Typography>
            )}
          </Box>

          <Select
            id="deviceType"
            name="deviceType"
            data-testid="firmwareversion-modal-select-deviceType"
            label={t('firmwareVersions.deviceType.label')}
            value={formData.deviceType}
            onChange={handleChange}
            options={deviceTypeOptions}
            error={!!errors.deviceType}
            helperText={errors.deviceType}
            required
            disabled={loading}
          />

          <Input
            id="note"
            name="note"
            data-testid="firmwareversion-modal-input-note"
            label={t('firmwareVersions.note')}
            value={formData.note}
            onChange={handleChange}
            multiline
            rows={3}
            disabled={loading}
            error={!!errors.note}
            helperText={errors.note || `${formData.note.length}/255`}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="firmwareversion-modal-button-cancel"
        >
          {t('firmwareVersions.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '') || loading}
          id="submit"
          name="submit"
          data-testid="firmwareversion-modal-button-submit"
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : editMode ? (
            t('firmwareVersions.save')
          ) : (
            t('firmwareVersions.add')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFirmwareVersionModal;
